// app/pages/LandingPage.tsx
// BrightPath Landing Page

import { Link } from 'react-router-dom';

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-cream-50 to-white">
      {/* Hero Section */}
      <header className="container mx-auto px-4 py-6">
        <nav className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-sage-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">B</span>
            </div>
            <span className="text-2xl font-display font-bold text-navy-900">
              BrightPath
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/resources"
              className="text-navy-700 hover:text-navy-900 font-medium"
            >
              Resources
            </Link>
            <Link
              to="/marketplace"
              className="text-navy-700 hover:text-navy-900 font-medium"
            >
              Marketplace
            </Link>
            <Link
              to="/login"
              className="text-navy-700 hover:text-navy-900 font-medium"
            >
              Sign In
            </Link>
            <Link
              to="/register"
              className="bg-sage-600 text-white px-4 py-2 rounded-lg hover:bg-sage-700 font-medium"
            >
              Get Started
            </Link>
          </div>
        </nav>
      </header>

      {/* Hero Content */}
      <main className="container mx-auto px-4 py-20">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-5xl font-display font-bold text-navy-900 mb-6">
            Supporting Families Through Every Step of the Journey
          </h1>
          <p className="text-xl text-navy-600 mb-8">
            BrightPath provides comprehensive tools and resources for families
            navigating complex medical situations. Track health, manage care teams,
            connect with resources, and find support—all in one place.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Link
              to="/register"
              className="bg-sage-600 text-white px-8 py-3 rounded-lg hover:bg-sage-700 font-medium text-lg"
            >
              Start Your Journey
            </Link>
            <Link
              to="/resources"
              className="border-2 border-navy-300 text-navy-700 px-8 py-3 rounded-lg hover:border-navy-400 font-medium text-lg"
            >
              Explore Resources
            </Link>
          </div>
        </div>

        {/* Feature Cards */}
        <div className="grid md:grid-cols-3 gap-8 mt-20">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-cream-200">
            <div className="w-12 h-12 bg-sage-100 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-sage-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-navy-900 mb-2">
              Health Tracking
            </h3>
            <p className="text-navy-600">
              Log symptoms, medications, and appointments. Generate reports for
              care team visits.
            </p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-cream-200">
            <div className="w-12 h-12 bg-terracotta-100 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-terracotta-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-navy-900 mb-2">
              Care Team Management
            </h3>
            <p className="text-navy-600">
              Coordinate with doctors, therapists, and specialists. Keep everyone
              on the same page.
            </p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-cream-200">
            <div className="w-12 h-12 bg-navy-100 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-navy-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-navy-900 mb-2">
              Resource Library
            </h3>
            <p className="text-navy-600">
              Access diagnosis-specific guides, therapy resources, and community
              support.
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-navy-900 text-white py-12 mt-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <p className="text-navy-300">
              © 2025 BrightPath. Supporting families every step of the way.
            </p>
          </div>

          {/* Developer Access - Remove before production */}
          <details className="border-t border-navy-700 pt-6">
            <summary className="cursor-pointer text-navy-400 hover:text-navy-300 text-sm font-medium">
              Developer Access
            </summary>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
              <Link
                to="/family/dashboard"
                className="block p-3 bg-navy-800 rounded-lg hover:bg-navy-700 transition-colors text-center"
              >
                <span className="block text-sage-400 text-xs uppercase tracking-wide mb-1">Portal</span>
                <span className="font-medium">Family</span>
              </Link>
              <Link
                to="/attorney/dashboard"
                className="block p-3 bg-navy-800 rounded-lg hover:bg-navy-700 transition-colors text-center"
              >
                <span className="block text-terracotta-400 text-xs uppercase tracking-wide mb-1">Portal</span>
                <span className="font-medium">Attorney</span>
              </Link>
              <Link
                to="/vendor/dashboard"
                className="block p-3 bg-navy-800 rounded-lg hover:bg-navy-700 transition-colors text-center"
              >
                <span className="block text-blue-400 text-xs uppercase tracking-wide mb-1">Portal</span>
                <span className="font-medium">Vendor</span>
              </Link>
              <Link
                to="/admin/dashboard"
                className="block p-3 bg-navy-800 rounded-lg hover:bg-navy-700 transition-colors text-center"
              >
                <span className="block text-yellow-400 text-xs uppercase tracking-wide mb-1">Portal</span>
                <span className="font-medium">Admin</span>
              </Link>
            </div>
            <p className="text-center text-navy-500 text-xs mt-4">
              Note: These links require authentication with the appropriate role.
            </p>
          </details>
        </div>
      </footer>
    </div>
  );
}